/**
 * Add to Cart component
 * * @module component-addtocart
 * @version 2.0.0
 * @extends HTMLElement
 */
class addToCartComponent extends HTMLElement {

    /**
     * Add to Cart web component.
     * * @constructor
     */
    constructor() {
        super();

        if (this.dataset.handle) {
            window.theme.product.loadProduct(this.dataset.handle, true).then(product => {
                if (!product) return;
                this.initSubmitButton(product);
                this.initEvents(product);
                // Call our new function after initialization
                this.updateButtonStateBasedOnSizeSelection(product);
            });
        } else {
            console.log('Missing product handle', this);
        }
    }

    /**
     * Init component events.
     * * @param {object} product Product data.
     * @returns {undefined}
     */
    initEvents(product) {
        // On submit add to cart form
        const form = this.getForm();
        if (form) {
            // Modify the form submit listener to include our validation first
            form.addEventListener('submit', event => this.onFormSubmit(event, product));
        }

        // On variant selector ready
        const variantSelector = this.querySelector('variant-selector[data-handle="' + this.dataset.handle + '"]')
            || document.querySelector('variant-selector[data-handle="' + this.dataset.handle + '"]');
        if (variantSelector) {
            variantSelector.addEventListener('ready', event => this.OnVariantSelectorReady(event, product));

            if (variantSelector.classList.contains('ready')) {
                this.OnVariantSelectorChange(variantSelector.querySelector('select'), product);
            }

            // Also listen to changes on the variant selector itself, which should trigger our button update
            // Note: Your theme already has an addEventListener for 'change' inside OnVariantSelectorChange.
            // We need to ensure our button state update runs after every relevant change.
            // Let's add a specific listener for radio button changes here, as your variant selector HTML is radio buttons.
            const sizeRadios = this.querySelectorAll('input[type="radio"][name="Size"]');
            sizeRadios.forEach(radio => {
                radio.addEventListener('change', () => this.updateButtonStateBasedOnSizeSelection(product));
            });
        }
    }

    /**
     * Toggle button if product select variant is out of stock.
     * This existing function handles stock, we will layer our size check on top.
     * * @param {object} select Product select variant.
     * @returns {undefined}
     */
    toggleButton(select, product) {
        let optionSelected = select.children[select.selectedIndex],
            outOfStockContent = this.querySelector('[data-outofstock-content]'),
            btn = this.getSubmitButton(),
            optionSelectedID = '',
            form = this.querySelector('form');


        if (optionSelected) {
            if (optionSelected.dataset.disabled === '') {
                optionSelected = false;
            } else {
                optionSelectedID = optionSelected.dataset.variantId;
                optionSelected = true;
            }
        } else {
            optionSelected = false;
        }

        // --- Original theme logic for stock (we keep this) ---
        if (optionSelected) {
            if (outOfStockContent)
                outOfStockContent.classList.remove('active');
            btn.disabled = false;
        } else {
            if (outOfStockContent)
                outOfStockContent.classList.add('active');
            btn.disabled = true;
        }
        // --- End Original theme logic ---

        if (form.dataset.editForm && form.dataset.editForm == optionSelectedID) {
            btn.disabled = true;
        }

        // START: Update max quantity on input
        if (optionSelectedID) {
            const quantityInput = this.querySelector('.quantity-selector__quantity');
            if (quantityInput) {
                let maxQty = '';
                if (product.variants_inventory && product.variants_inventory[optionSelectedID]) {
                    const inv = product.variants_inventory[optionSelectedID];
                    if (inv.management === 'shopify' && inv.policy === 'deny') {
                        maxQty = inv.quantity;
                    }
                } else {
                     const currentVariant = product.variants && product.variants.find(v => v.id == optionSelectedID);
                      if (currentVariant && currentVariant.inventory_management === 'shopify' && currentVariant.inventory_policy === 'deny') {
                        maxQty = currentVariant.inventory_quantity;
                      }
                }

                if (maxQty !== '') {
                    quantityInput.setAttribute('max', maxQty);
                    if (parseInt(quantityInput.value) > maxQty) {
                        quantityInput.value = maxQty;
                    }
                } else {
                    quantityInput.removeAttribute('max');
                }
            }
        }
        // END: Update max quantity on input

        // Call our function here to update text based on size selection
        this.updateButtonStateBasedOnSizeSelection(product);
    }

    /**
     * Get component template
     * * @returns {string} Component HTML template.
     */
    getTemplate() {
        if (this.querySelector('button[type="submit"][data-edit-btn]') && this.querySelector('button[type="submit"][data-edit-btn]').innerHTML) {
            return this.querySelector('button[type="submit"]').innerHTML;
        }
        return window.theme.addToCart.template ? window.theme.addToCart.template : '';
    }

    /**
     * Init add to cart button features
     * * @param {object} product Product data.
     * @returns {undefined}
     */
    initSubmitButton(product) {
        let html = this.getTemplate(),
            btn = this.getSubmitButton();

        if (html && btn) {
            btn.classList.add('product-addtocart__button');
            btn.classList.add('btn-icon');
            btn.classList.add('btn-icon-before');
            btn.classList.add('btn-loading');

            if (product.available) {
                btn.classList.add('active');
                btn.disabled = false;
                
            } else {
                btn.disabled = true;
            }

            // Inject HTML
            btn.innerHTML = html;
        }
    }

    getForm() {
        return this.querySelector('form');
    }

    getSubmitButton() {
        return this.querySelector('button[type="submit"]');
    }

    async onFormSubmit(event, product) {
        event.preventDefault()
        const form = event.target;

        let editedCartLineKey = null;

        // Our validation logic (now part of the component)
        const sizeRadios = this.querySelectorAll('input[type="radio"][name="Size"]');
        // Start new logic to handle variants with multiple options
        const variantOptions = Object.keys(product.options_by_name);

        let areAllVariantsSelected = true;
        let missingVariantOptionName = '';

        for (let i = 0; i < variantOptions.length; i++) {
            const isVariantOptionPresent = this.querySelectorAll(`input[type="radio"][name="${variantOptions[i]}"]`);
            let isVariantOptionSelected = false;

            if (isVariantOptionPresent && isVariantOptionPresent?.length > 0) {
                // If there is ONLY ONE value for this option, do not wait for the user to select it.
                if (isVariantOptionPresent.length === 1) {
                    isVariantOptionSelected = true;
                } else {
                    // If multiple options are present, check if one is actually selected
                    for (let j = 0; j < isVariantOptionPresent.length; j++) {
                        if (isVariantOptionPresent[j].checked) {
                            isVariantOptionSelected = true;
                            break;
                        }
                    }
                }

                if (!isVariantOptionSelected) {
                    // If no variant option is selected, disable the add to cart button
                    form.classList.add('disabled');
                    areAllVariantsSelected = false;
                    missingVariantOptionName = variantOptions[i];
                    break;
                }
            } else {
                // If no size options are present, consider a size to be "selected" by default
                // (i.e., nothing for the user to choose, so it's ready to add to cart)
                isVariantOptionSelected = true;
            }
        }

        if (!areAllVariantsSelected) {
            event.preventDefault(); // Stop the form from submitting

            // If not all variants are selected, disable the add to cart button
            form.classList.add('disabled');

            let errorMessageContainer = form.querySelector('.product-form__error-message');
            if (!errorMessageContainer) {
                errorMessageContainer = document.createElement('div');
                errorMessageContainer.className = 'product-form__error-message';
                errorMessageContainer.style.color = 'red';
                errorMessageContainer.style.marginTop = '10px';
                errorMessageContainer.style.marginBottom = '10px';
                errorMessageContainer.textContent = `Please select a ${missingVariantOptionName}.`;
                
                const addToCartButton = this.getSubmitButton(); // Use getSubmitButton from the component
                if (addToCartButton) {
                    addToCartButton.parentNode.insertBefore(errorMessageContainer, addToCartButton);
                } else {
                    form.appendChild(errorMessageContainer);
                }
            } else {
                errorMessageContainer.textContent = `Please select a ${missingVariantOptionName}.`;
                errorMessageContainer.style.display = 'block';
            }

            if (errorMessageContainer) {
                errorMessageContainer.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }

        // --- START NEW LOGIC FOR HANDLING PRODUCTS WITH NO SIZE OPTIONS ---
        let isSizeOptionPresent = sizeRadios.length > 0; // Check if there are any size options at all
        let isSizeSelected = false;

        if (isSizeOptionPresent) {
            // If size options are present, check if one is actually selected
            for (let i = 0; i < sizeRadios.length; i++) {
                if (sizeRadios[i].checked) {
                    isSizeSelected = true;
                    break;
                }
            }
        } else {
            // If no size options are present, consider a size to be "selected" by default
            // (i.e., nothing for the user to choose, so it's ready to add to cart)
            isSizeSelected = true; 
        }
        // --- END NEW LOGIC FOR HANDLING PRODUCTS WITH NO SIZE OPTIONS ---

        if (!isSizeSelected) { // This condition now correctly factors in whether options exist
            event.preventDefault(); // Stop the form from submitting

            let errorMessageContainer = form.querySelector('.product-form__error-message');
            if (!errorMessageContainer) {
                errorMessageContainer = document.createElement('div');
                errorMessageContainer.className = 'product-form__error-message';
                errorMessageContainer.style.color = 'red';
                errorMessageContainer.style.marginTop = '10px';
                errorMessageContainer.style.marginBottom = '10px';
                errorMessageContainer.textContent = 'Please select a Size.';
                
                const addToCartButton = this.getSubmitButton(); // Use getSubmitButton from the component
                if (addToCartButton) {
                    addToCartButton.parentNode.insertBefore(errorMessageContainer, addToCartButton);
                } else {
                    form.appendChild(errorMessageContainer);
                }
            } else {
                errorMessageContainer.textContent = 'Please select a Size.';
                errorMessageContainer.style.display = 'block';
            }

            if (errorMessageContainer) {
                errorMessageContainer.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return; // Important: Stop here if size is not selected
        } else {
            // check if product is already in cart
            const selectedProductId = product.id

            let selectedProductVariantId = null;
            const cartDataResponse = await fetch(window.Shopify.routes.root + 'cart.js');
            const cart = await cartDataResponse.json();

            if (form.dataset.editForm) {
                const editedCartLine = cart.items.find(item => String(item.id) === String(form.dataset.editForm));
                if (editedCartLine) editedCartLineKey = editedCartLine.key;
            }

            const serializedData = window.theme.serialize.serializeForm(form);
            // Use URLSearchParams to parse the serialized data
            const params = new URLSearchParams(serializedData);

            // Extract the value of 'id'
            selectedProductVariantId = params.get('id');

            if (selectedProductVariantId) {
                selectedProductVariantId = parseFloat(selectedProductVariantId);
            }

            const selectedVariant = product.variants.find(v => v.id === selectedProductVariantId);
            if (selectedVariant && !selectedVariant.available) {
                event.preventDefault();
                form.classList.add('disabled');

                let errorMessageContainer = form.querySelector('.product-form__error-message');
                if (!errorMessageContainer) {
                    errorMessageContainer = document.createElement('div');
                    errorMessageContainer.className = 'product-form__error-message';
                    errorMessageContainer.style.color = 'red';
                    errorMessageContainer.style.marginTop = '10px';
                    errorMessageContainer.style.marginBottom = '10px';
                    errorMessageContainer.textContent = 'This option is out of stock.';

                    const addToCartButton = this.getSubmitButton();
                    if (addToCartButton) {
                        addToCartButton.parentNode.insertBefore(errorMessageContainer, addToCartButton);
                    } else {
                        form.appendChild(errorMessageContainer);
                    }
                } else {
                    errorMessageContainer.textContent = 'This option is out of stock.';
                    errorMessageContainer.style.display = 'block';
                }

                if (errorMessageContainer) {
                    errorMessageContainer.scrollIntoView({ behavior: "smooth", block: "center" });
                }
                return;
            }

            const cartItem = cart.items.find(item => item.variant_id === selectedProductVariantId);

            // get variant stock from <span id="stock-count">{{ variant.inventory_quantity }}</span> tag
            const selectVariantStockLevel = document.querySelector(`[data-variant-stockId="${selectedProductVariantId}"]`);
            const variantStockLevel = parseInt(selectVariantStockLevel?.dataset?.variantStocklevel) || (cartItem?.quantity + 1);

            // check if selected variant is already in cart and if no more quantity is available to be added to cart
            if (selectedProductVariantId && selectedProductVariantId === cartItem?.variant_id && variantStockLevel <= cartItem?.quantity) {
                event.preventDefault(); // Stop the form from submitting

                // If not all variants are selected, disable the add to cart button
                form.classList.add('disabled');

                let longErrorText = `You already have the maximum quantity of ${cartItem?.variant_title} in your cart.`
                let shortErrorText = `Maximum quantity in cart already.`
                let message = form.querySelector('[data-error-message]');

                if (message && window.screenMode != 'mobile') {
                    message.innerHTML = '';
                    message.append(shortErrorText);
                    message.classList.add("enabled");
                    message.style.width = 'fit-content';
                }
                let errorMessageContainer = form.querySelector('.product-form__error-message');
                if (!errorMessageContainer) {
                    errorMessageContainer = document.createElement('div');
                    errorMessageContainer.className = 'product-form__error-message';
                    errorMessageContainer.style.color = 'red';
                    errorMessageContainer.style.marginTop = '10px';
                    errorMessageContainer.style.marginBottom = '10px';
                    errorMessageContainer.textContent = longErrorText;
                    
                    const addToCartButton = this.getSubmitButton(); // Use getSubmitButton from the component
                    if (addToCartButton) {
                        addToCartButton.parentNode.insertBefore(errorMessageContainer, addToCartButton);
                    } else {
                        form.appendChild(errorMessageContainer);
                    }
                } else {
                    errorMessageContainer.textContent = longErrorText;
                    errorMessageContainer.style.display = 'block';
                }

                if (errorMessageContainer) {
                    errorMessageContainer.scrollIntoView({ behavior: "smooth", block: "center" });
                }
                return;
            }
            const errorMessageContainer = form.querySelector('.product-form__error-message');
            if (errorMessageContainer) {
                errorMessageContainer.style.display = 'none';
            }
        }
        // --- End our validation logic ---


        event.preventDefault(); // Keep this original line

        // Find the product's availability

        if (!product.available) {
            // If out of stock, show the out of stock message
            let errorMessageContainer = form.querySelector('.product-form__error-message');
            if (!errorMessageContainer) {
                errorMessageContainer = document.createElement('div');
                errorMessageContainer.className = 'product-form__error-message';
                errorMessageContainer.style.color = 'red';
                errorMessageContainer.style.marginTop = '10px';
                errorMessageContainer.style.marginBottom = '10px';
                errorMessageContainer.textContent = 'Product is out of stock.';
                
                const addToCartButton = this.getSubmitButton(); // Use getSubmitButton from the component
                if (addToCartButton) {
                    addToCartButton.parentNode.insertBefore(errorMessageContainer, addToCartButton);
                } else {
                    form.appendChild(errorMessageContainer);
                }
            } else {
                errorMessageContainer.textContent = 'Product is out of stock.';
                errorMessageContainer.style.display = 'block';
            }

            if (errorMessageContainer) {
                errorMessageContainer.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return; // Important: Stop here if size is not selected
        }
        
        let button = form.querySelector('[type="submit"]'),
            validate = this.validateFieldRequired(form),
            message = form.querySelector('[data-error-message]');

        if (!validate && button) { // This `!validate` check is for existing required fields
            button.setAttribute('disabled', true);
            
            window.theme.cart.addByForm(form).then(result => {
                button.removeAttribute('disabled');

                if (message) {
                    message.innerHTML = '';
                    message.classList.remove("enabled");
                }

                if (form.dataset.editForm) {
                    const editModal = document.querySelector(`[data-edit-modal="${form.dataset.editForm}"]`);
                    if (editModal) editModal.classList.remove('active');

                    if (editedCartLineKey) {
                        window.theme.cart.update(editedCartLineKey, 0)
                            .then(() => window.theme.mini_cart._updateMinicartData())
                            .catch(error => console.log(error));
                    }
                }

                let closeQuickView = new CustomEvent('closeQuickView');
                window.dispatchEvent(closeQuickView);

            }).catch(error => {
                button.removeAttribute('disabled');

                if (message) {
                    message.innerHTML = '';
                    // Only the server message is safe to show, anything else is an internal exception
                    message.append(error.isFromServer ? error.message : 'Something went wrong. Please try again.');
                    message.classList.add("enabled");
                    message.style.width = 'fit-content';

                    if (window.screenMode == 'mobile') {
                        message.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
                    }
                }
            });
        }
    }

    OnVariantSelectorReady(event, product) {
        let select = event.target.querySelector('select');
        this.OnVariantSelectorChange(select, product);
    }

    OnVariantSelectorChange(select, product) {
        let outOfStockContent = this.querySelector('[data-outofstock-content]'),
            btn = this.getSubmitButton();

        if (!btn) {
            return;
        }

        // This block handles stock availability; our new function will handle size text
        if (product.available) {
            if (outOfStockContent)
                outOfStockContent.classList.remove('active');
            // btn.disabled = false; // We'll let updateButtonStateBasedOnSizeSelection handle this
        } else {
            if (outOfStockContent)
                outOfStockContent.classList.add('active');
            // btn.disabled = true; // We'll let updateButtonStateBasedOnSizeSelection handle this
        }

        // Check if the select variant changes
        select.addEventListener('change', event => {
            this.toggleButton(event.target, product);
            
            const sizeErrorMessage = this.querySelector('[data-error-message]');
            if (sizeErrorMessage) {
                sizeErrorMessage.innerHTML = '';
                sizeErrorMessage.classList.remove("enabled");
            }
            // Add call to update button text on variant change
            this.updateButtonStateBasedOnSizeSelection(product);
        });
        
        setTimeout(() => {
            this.updateButtonStateBasedOnSizeSelection(product);
        }, 300);
        setTimeout(() => {
            this.updateButtonStateBasedOnSizeSelection(product);
        }, 400);
        setTimeout(() => {
            this.updateButtonStateBasedOnSizeSelection(product);
        }, 1000);
        // Initial update after variant selector changes or is ready
        this.updateButtonStateBasedOnSizeSelection(product);
    }

    validateFieldRequired(form) {
        const fields = form.querySelectorAll("[required]");
        let foundError = false;

        for (let field of fields) {
            foundError = field.validity.valueMissing;
            if (foundError) {
                field.classList.add("invalid");
            }
        }
        return foundError;
    }

    /**
     * NEW FUNCTION: Updates the Add to Bag button text and state based on size selection.
     */
    updateButtonStateBasedOnSizeSelection(product) {
        const addToCartButton = this.getSubmitButton(); // Use the component's method
        const addToCartContent = addToCartButton ? addToCartButton.querySelector('[data-add-to-card-content]') : null;
        const soldOutContent = addToCartButton ? addToCartButton.querySelector('[data-outofstock-content]') : null;
        const sizeRadios = this.querySelectorAll('input[type="radio"][name="Size"]');

        if (!addToCartButton || !addToCartContent) {
            console.warn('Add to Bag button or its content div not found within component. Cannot update text.');
            return;
        }


        // Start New Logic - Check if any variants are unselected
        
        // Handle variants with multiple options
        const variantOptions = Object.keys(product.options_by_name);

        let areAllVariantsSelected = true;
        let missingVariantOptionName = '';
        let currentSelectedOptions = [];

        for (let i = 0; i < variantOptions.length; i++) {
            const isVariantOptionPresent = this.querySelectorAll(`input[type="radio"][name="${variantOptions[i]}"]`);
            let isVariantOptionSelected = false;
            let selectedValue = null;

            if (isVariantOptionPresent && isVariantOptionPresent?.length > 0) {
                // If there is ONLY ONE value for this option, do not wait for the user to select it.
                // Treat it as logically selected.
                if (isVariantOptionPresent.length === 1) {
                    isVariantOptionSelected = true;
                    selectedValue = isVariantOptionPresent[0].value;
                } else {
                    // If multiple options are present, check if one is actually selected
                    for (let j = 0; j < isVariantOptionPresent.length; j++) {
                        if (isVariantOptionPresent[j].checked) {
                            isVariantOptionSelected = true;
                            selectedValue = isVariantOptionPresent[j].value;
                            break;
                        }
                    }
                }

                if (!isVariantOptionSelected) {
                    areAllVariantsSelected = false;
                    if (!missingVariantOptionName) {
                        missingVariantOptionName = variantOptions[i];
                    }
                }
            } else {
                // If no options are present in DOM, consider it selected (e.g. single default value)
                isVariantOptionSelected = true;
                const optionValues = product.options_by_name[variantOptions[i]];
                if (optionValues && optionValues.length > 0) {
                    selectedValue = optionValues[0];
                }
            }
            currentSelectedOptions.push(selectedValue);
        }

        // Check if ANY available variant matches the currently selected exact options
        let hasAvailableMatchingVariant = true;
        
        if (product && product.variants) {
            hasAvailableMatchingVariant = product.variants.some(variant => {
                let matches = true;
                for (let k = 0; k < currentSelectedOptions.length; k++) {
                   if (currentSelectedOptions[k] !== null) {
                       const variantOptionValue = variant.options ? variant.options[k] : variant[`option${k+1}`];
                       if (variantOptionValue !== currentSelectedOptions[k]) {
                           matches = false;
                           break;
                       }
                   }
                }
                return matches && variant.available;
            });
        }

        const isSoldOutByTheme = soldOutContent && soldOutContent.classList.contains('active');

        // If NO remaining options can yield an available variant, then it's definitively Sold Out
        if (!hasAvailableMatchingVariant || (areAllVariantsSelected && isSoldOutByTheme)) {
            if (soldOutContent) soldOutContent.style.display = 'block';
            if (addToCartContent) addToCartContent.style.display = 'none';
            addToCartButton.disabled = true;
            addToCartButton.classList.remove('active');
            return;
        }

        if (!areAllVariantsSelected) {
            addToCartContent.textContent = `Please select ${missingVariantOptionName}`;
            addToCartContent.style.display = 'block';
            if (soldOutContent) soldOutContent.style.display = 'none'; // Ensure sold out is hidden
            addToCartButton.disabled = true;
            addToCartButton.classList.remove('active'); // Indicate it's not ready for purchase
            return;
        }

        // If not sold out and all options selected, handle Add to Bag state
        addToCartContent.textContent = 'Add to Bag';
        addToCartContent.style.display = 'block';
        if (soldOutContent) soldOutContent.style.display = 'none'; // Ensure sold out is hidden if size is selected
        addToCartButton.disabled = false;
        addToCartButton.classList.add('active'); // Re-enable its active styling/functionality
    }
}

customElements.define('component-addtocart', addToCartComponent);